import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import styles from './UsersSnapshot.module.css';
import BlankState from '../BlankState/BlankState';
import { useUserStats } from '../../../api/users/useUsersApi';
import { useAuditLogs } from '../../../api/users/useAuditApi';
import { fmtInt, timeAgo } from '../../../utils/moneyFormat';

// Full-width panel, so it reads across rather than down: the account figures
// hold a quiet left column and the audit trail takes the room it needs on the
// right. No loading prop here — the panel fetches its own data, so it drives
// its own skeletons and shows an em dash for anything that resolves to nothing.
const EM_DASH = '—';
const SKELETONS = [0, 1, 2, 3];

const UsersSnapshot = () => {
  const navigate = useNavigate();
  const { data: stats, loading: statsLoading } = useUserStats();
  const { data: logs, loading: logsLoading } = useAuditLogs({ limit: 4 });

  const adminCount =
    (stats?.roleDistribution?.superAdmin ?? 0) + (stats?.adminUsers ?? 0);

  const metrics = [
    { label: 'Total users', value: stats ? fmtInt(stats.totalUsers) : EM_DASH },
    { label: 'Active', value: stats ? fmtInt(stats.activeUsers) : EM_DASH },
    { label: 'Admins', value: stats ? fmtInt(adminCount) : EM_DASH },
  ];

  const entries = Array.isArray(logs) ? logs.slice(0, 4) : [];

  return (
    <div className={`dshPanel ${styles.root}`}>
      <div className="dshHead">
        <div>
          <h3 className="dshTitle">Users &amp; Access</h3>
          <p className="dshSub">Accounts, roles &amp; recent changes</p>
        </div>
        <button
          type="button"
          className="dshAction"
          onClick={() => navigate('/users/overview')}
        >
          Manage
          <ArrowRight size={13} strokeWidth={2.2} />
        </button>
      </div>

      <div className={styles.grid}>
        <div className={styles.metrics}>
          {metrics.map((m) => (
            <div key={m.label} className={styles.metric}>
              <span className="dshEyebrow">{m.label}</span>
              {statsLoading ? (
                <span className={`dshSkel ${styles.skelMetric}`} />
              ) : (
                <span className={`dshNum ${styles.metricValue}`}>{m.value}</span>
              )}
            </div>
          ))}
        </div>

        <div className={styles.log}>
          <span className={`dshEyebrow ${styles.logHead}`}>Recent changes</span>

          {logsLoading ? (
            <div className={styles.rows}>
              {SKELETONS.map((i) => (
                <div key={i} className={`dshRow ${styles.entry}`}>
                  <span className={styles.entryMain}>
                    <span className={`dshSkel ${styles.skelAction}`} />
                    <span className={`dshSkel ${styles.skelActor}`} />
                  </span>
                  <span className={`dshSkel ${styles.skelTime}`} />
                </div>
              ))}
            </div>
          ) : entries.length === 0 ? (
            <BlankState
              icon={ShieldCheck}
              title="No account changes yet"
              text="User, role and permission changes are recorded here with who made them."
            />
          ) : (
            <div className={styles.rows}>
              {entries.map((log) => (
                <div key={log.id} className={`dshRow ${styles.entry}`}>
                  <span className={styles.entryMain}>
                    <span className={`dshTrunc ${styles.action}`}>{log.action}</span>
                    <span className={`dshTrunc ${styles.actor}`}>
                      {log.user?.name || 'System'}
                    </span>
                  </span>
                  <span className={`dshNum ${styles.time}`}>
                    {timeAgo(log.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersSnapshot;
